import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@/components/ui';
import { toast } from '@/stores';
import { settingsService } from '@/services';
import { useAhaWalkthrough } from '@/hooks';
import { resyncGmailFilters } from '@/services/gmail-forwarding.service';
import type { ApprovedSender } from '@/services/settings.service';
import { config } from '@/lib/config';
import { Step0Prompt } from './Step0Prompt';
import { Step1CheckSenders } from './Step1CheckSenders';
import { Step2ForwardEmail } from './Step2ForwardEmail';
import { Step3WatchItAppear } from './Step3WatchItAppear';
import { LeaveWithoutSavingModal } from './LeaveWithoutSavingModal';
import type { WalkthroughSenderListItem } from './WalkthroughSenderList';

// Demo videos for the carousel. Sourced from the connected Supabase
// project's public `media` bucket so the same code resolves to the
// right environment (staging/prod) without per-env config — promote
// the assets to the prod bucket as part of release.
const AHA_STEP2_VIDEO = `${config.supabaseUrl}/storage/v1/object/public/media/aha_step2.mp4`;
const AHA_STEP3_VIDEO = `${config.supabaseUrl}/storage/v1/object/public/media/aha_step3.mp4`;

/**
 * PR #181 (feat/auto-approve-connected-gmail) adds `system_managed` to
 * `approved_senders` for the OAuth-seeded forwarding address. We
 * surface that row in the walkthrough list with a "Required" badge
 * (kelvin's review) so the user understands it can't be removed.
 * Widen the row type locally for compatibility with environments that
 * haven't run the PR #181 migration yet — missing → treated as a
 * regular user-picked sender, which is the right fallback.
 */
type SenderWithFlags = ApprovedSender & { system_managed?: boolean };

function toItem(r: SenderWithFlags): WalkthroughSenderListItem {
  return {
    id: r.id,
    type: r.type as 'domain' | 'email',
    value: r.value,
    required: r.system_managed === true,
  };
}

export interface AhaWalkthroughModalProps {
  /** When the host opens the walkthrough manually (Settings re-entry). */
  open: boolean;
  /** Called when the modal fully closes for any reason. */
  onClose: () => void;
  householdId: string;
  /** Force-start at Step 1 — used when the user opens via Settings (skip Step 0). */
  startAtStep?: 0 | 1;
}

type CarouselStep = 1 | 2 | 3;

/**
 * Walkthrough orchestrator (YAR-238). Owns:
 *   - sender list fetch + batch save through `settings.service`
 *   - state-machine writes via `useAhaWalkthrough`
 *   - step navigation + the X / back behaviours per AC 10/11/14/15
 *
 * The Today Tab mounts this as `<AhaWalkthroughModal open={shouldShow}
 * onClose={...} householdId={hid} />`. The Settings re-entry mounts the
 * same component with `startAtStep={1}` so the Step 0 prompt is skipped.
 */
export function AhaWalkthroughModal({
  open,
  onClose,
  householdId,
  startAtStep,
}: AhaWalkthroughModalProps) {
  const { markStarted, markDismissed, markCompleted } = useAhaWalkthrough({
    householdId,
  });

  // `phase` is 'step0' when we're showing the initial prompt, then a
  // CarouselStep once the user enters the flow. Decoupled from the
  // persisted state machine so re-renders driven by `useAhaWalkthrough`
  // don't yank the user out of mid-flow.
  const [phase, setPhase] = useState<'step0' | CarouselStep>('step0');
  const [senders, setSenders] = useState<WalkthroughSenderListItem[]>([]);
  const [sendersLoaded, setSendersLoaded] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Track whether Step 1 is in its Edit Senders sub-state so any close
  // vector (X, backdrop, Escape) can route through the LeaveWithoutSaving
  // confirm while editing (AC 10).
  const [step1Editing, setStep1Editing] = useState(false);

  // Reset internal state every time the host re-opens the modal so a
  // user who dismissed, then re-entered via Settings, doesn't pick up
  // the previous carousel position.
  useEffect(() => {
    if (!open) return;
    setPhase(startAtStep === 1 ? 1 : 'step0');
    setConfirmLeave(false);
    setStep1Editing(false);
  }, [open, startAtStep]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSendersLoaded(false);
    settingsService
      .getApprovedSenders(householdId)
      .then((rows) => {
        if (cancelled) return;
        // Include system_managed rows (PR #181 seeds the user's own
        // Gmail there) and tag them as `required` so the walkthrough
        // renders the Required badge instead of a trash icon.
        setSenders((rows as SenderWithFlags[]).map(toItem));
        setSendersLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[AhaWalkthrough] Failed to load senders:', err);
        toast.error('Could not load your approved senders');
        setSendersLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, householdId]);

  // ── Step 0 handlers ─────────────────────────────────────────────────
  const handleShowMeHow = useCallback(async () => {
    await markStarted();
    setPhase(1);
  }, [markStarted]);

  const handleStep0NotNow = useCallback(async () => {
    await markDismissed();
    onClose();
  }, [markDismissed, onClose]);

  // ── Step 1 handlers ─────────────────────────────────────────────────
  const handleStep1Close = useCallback(
    (info: { editing: boolean }) => {
      // AC 10: any close while editing pops the LeaveWithoutSaving confirm.
      if (info.editing) {
        setConfirmLeave(true);
        return;
      }
      // Clean close on Step 1 (default sub-state): tapping X is still an
      // explicit dismissal of the walkthrough as a whole.
      void (async () => {
        await markDismissed();
        onClose();
      })();
    },
    [markDismissed, onClose],
  );

  const handleStep1EditStateChange = useCallback((editing: boolean) => {
    setStep1Editing(editing);
  }, []);

  const handleConfirmLeave = useCallback(async () => {
    // AC 10: "Leave" discards changes, closes the carousel, writes
    // dismissed state.
    setConfirmLeave(false);
    await markDismissed();
    onClose();
  }, [markDismissed, onClose]);

  const handleKeepEditing = useCallback(() => {
    setConfirmLeave(false);
  }, []);

  const handleSaveSenders = useCallback(
    async (next: WalkthroughSenderListItem[]) => {
      // Compute add / remove delta against the persisted set. Required
      // rows (system_managed) are explicitly excluded from the delete
      // side — the UI swaps the trash for a Required badge, so they
      // shouldn't ever be missing from `next`, but belt-and-suspenders
      // here: RLS would reject the DELETE and the toast would lie.
      const persistedById = new Map(senders.map((s) => [s.id, s]));
      const draftById = new Map(next.map((s) => [s.id, s]));

      const toAdd = next.filter((s) => s.id.startsWith('tmp-'));
      const toRemove = senders.filter(
        (s) => !draftById.has(s.id) && !s.required,
      );
      // Items that exist in both maps with the same identity stay.
      void persistedById;

      try {
        for (const sender of toAdd) {
          await settingsService.addApprovedSender(
            householdId,
            sender.type,
            sender.value,
          );
        }
        for (const sender of toRemove) {
          await settingsService.removeApprovedSender(sender.id);
        }

        // Refresh from the server so we pick up the real ids of the new
        // rows (replaces the `tmp-*` placeholders).
        const fresh = await settingsService.getApprovedSenders(householdId);
        setSenders((fresh as SenderWithFlags[]).map(toItem));

        // Resync the Gmail filter so the inbox actually reflects the new
        // list (same path used by Settings → Approved Senders).
        try {
          const result = await resyncGmailFilters(householdId);
          if (!result.ok && result.needs_oauth) {
            toast.default(
              'Reconnect Gmail to apply the new sender list to your inbox',
            );
          }
        } catch (err) {
          console.warn('[AhaWalkthrough] resync failed:', err);
        }
        toast.success('Senders updated');
      } catch (err) {
        console.error('[AhaWalkthrough] save senders failed:', err);
        toast.error('Could not save senders');
        throw err;
      }
    },
    [householdId, senders],
  );

  const handleConfirmSenders = useCallback(() => {
    setPhase(2);
  }, []);

  // ── Step 2 / Step 3 handlers ────────────────────────────────────────
  const handleStep2Back = useCallback(() => setPhase(1), []);
  const handleStep2Next = useCallback(() => setPhase(3), []);
  const handleStep3Back = useCallback(() => setPhase(2), []);

  const handleStep2or3Close = useCallback(() => {
    // AC 11: X on Step 2/3 closes without confirmation, fires a toast,
    // does NOT write dismissed state. The walkthrough is `started` at
    // this point (markStarted was written on "Show me how") so the
    // auto-trigger won't fire again, but Settings re-entry works.
    toast.default('You can find this guide again in Settings');
    onClose();
  }, [onClose]);

  const handleDone = useCallback(async () => {
    await markCompleted();
    onClose();
  }, [markCompleted, onClose]);

  // ── Render ──────────────────────────────────────────────────────────
  if (!open) return null;

  if (phase === 'step0') {
    return (
      <Step0Prompt
        open
        onShowMeHow={handleShowMeHow}
        onNotNow={handleStep0NotNow}
      />
    );
  }

  // The carousel uses a single Modal with bare content; the step
  // components render their own header/body/footer. Guard against the
  // initial sender fetch so Step 1 doesn't briefly show the empty state
  // for a user who actually has senders.
  return (
    <>
      <Modal
        open
        onClose={() => {
          // Funnel backdrop/Escape through the step-aware close logic.
          if (phase === 1) {
            handleStep1Close({ editing: step1Editing });
          } else {
            handleStep2or3Close();
          }
        }}
        bare
      >
        {!sendersLoaded ? (
          <div className="flex items-center justify-center p-10">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : phase === 1 ? (
          <Step1CheckSenders
            senders={senders}
            onConfirm={handleConfirmSenders}
            onCloseRequest={handleStep1Close}
            onEditStateChange={handleStep1EditStateChange}
            onSaveSenders={handleSaveSenders}
          />
        ) : phase === 2 ? (
          <Step2ForwardEmail
            senders={senders}
            onBack={handleStep2Back}
            onNext={handleStep2Next}
            onClose={handleStep2or3Close}
            videoSrc={AHA_STEP2_VIDEO}
          />
        ) : (
          <Step3WatchItAppear
            onBack={handleStep3Back}
            onDone={handleDone}
            onClose={handleStep2or3Close}
            videoSrc={AHA_STEP3_VIDEO}
          />
        )}
      </Modal>

      <LeaveWithoutSavingModal
        open={confirmLeave}
        onLeave={handleConfirmLeave}
        onKeepEditing={handleKeepEditing}
      />
    </>
  );
}
